import apiClient from "../../services/apiClient";
import { useState, useEffect } from "react";
import { getCookie } from "../../utils/cookies";
import toast from "../../utils/toast";

function getInitials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  return parts
    .slice(0, 2)
    .map((p) => p[0])
    .join("")
    .toUpperCase();
}

const KYC_STATUS_LABELS = {
  1: "Incomplete",
  2: "Applied",
  3: "Completed",
  4: "Re-KYC",
  5: "Rejected",
};

const KYC_UPDATE_ALLOWED_STATUSES = [1, 4, 5];

const DOC_BASE_URL = "https://api.learning2earning.net";

export default function Profile() {
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState(null);

  const [isKycModalOpen, setIsKycModalOpen] = useState(false);
  const [isKycLoading, setIsKycLoading] = useState(false);
  const [kycDocs, setKycDocs] = useState([]);
  const [docFiles, setDocFiles] = useState({});
  const [isApplyingKyc, setIsApplyingKyc] = useState(false);
  const [isUploadingProfilePic, setIsUploadingProfilePic] = useState(false);

  useEffect(() => {
    fetchProfileData();
  }, []);

  const fetchProfileData = async () => {
    setIsLoading(true);
    try {
      const userData = JSON.parse(getCookie("userData") || "{}");

      const res = await apiClient.post("/App/GetProfile", {
        appid: "ROUNDPAYAPPID13APR20191351",
        imei: "d593bdd26dfbea56",
        loginTypeID: 1,
        regKey: "",
        serialNo: "d593bdd26dfbea56",
        session: getCookie("session") || "",
        sessionID: getCookie("sessionID") || "",
        userID: userData.userID || "",
        version: "1.0",
      });

      if (res.statuscode == 1) {
        const info = res.userInfo || {};
        setFormData({
          profilePic: info.profilePic
            ? `${info.profilePic.startsWith("https") ? info.profilePic : `${DOC_BASE_URL}${info.profilePic}`}?t=${Date.now()}`
            : "",
          name: info.name || "",
          dob: info.dob || "",
          mobileNo: info.mobileNo || "",
          alternateMobile: info.alternateMobile || "",
          emailID: info.emailID || "",
          pincode: info.pincode || "",
          address: info.address || "",
          city: info.city || "",
          stateName: info.stateName || "",
          landmark: (info.landmark || "").trim(),
          pan: info.pan || "",
          aadhar: info.aadhar || "",
          bankName: info.bankName || "",
          branchName: info.branchName || "",
          ifsc: info.ifsc || "",
          accountNumber: info.accountNumber || "",
          accountName: info.accountName || "",
          isDoubleFactor: !!info.isDoubleFactor,
          kycStatus: info.kycStatus ?? 0,
          commRate: info.commRate ?? 0,
          gstin: info.gstin || "",
          isGSTApplicable: !!info.isGSTApplicable,
          isTDSApplicable: !!info.isTDSApplicable,
          locationType: info.locationType || "",
          outletName: info.outletName || "",
          poupulation: info.poupulation || "",
          qualification: info.qualification || "",
          shoptype: info.shopType || "",
          outletID: info.outletID ?? 0,
        });
      } else {
        toast.error(res.msg || "Failed to load profile");
      }
    } catch (err) {
      console.log("error", err);
    } finally {
      setIsLoading(false);
    }
  };

  function handleChange(field, value) {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }

  async function handleProfilePicChange(file) {
    if (!file) return;

    const previewUrl = URL.createObjectURL(file);
    setFormData((prev) => ({ ...prev, profilePic: previewUrl }));
    setIsUploadingProfilePic(true);

    try {
      const userData = JSON.parse(getCookie("userData") || "{}");

      const userRequest = {
        ID: 0,
        IsB2C: false,
        TID: 0,
        appid: "ROUNDPAYAPPID13APR20191351",
        id: 0,
        imei: "d593bdd26dfbea56",
        loginTypeID: 1,
        regKey: "",
        serialNo: "d593bdd26dfbea56",
        session: getCookie("session") || "",
        sessionID: getCookie("sessionID") || "",
        uid: 0,
        userID: userData.userID || "",
        version: "1.0",
      };

      const uploadPayload = new FormData();
      uploadPayload.append("file", file, file.name);
      uploadPayload.append("userRequest", JSON.stringify(userRequest));

      const res = await apiClient.post("/App/UploadProfile", uploadPayload, {
        headers: { "Content-Type": undefined },
      });

      if (res.statuscode == 1) {
        toast.success(res.msg || "Profile picture updated");
        await fetchProfileData();
      } else {
        toast.error(res.msg || "Failed to upload profile picture");
      }
    } catch (err) {
      console.log("error", err);
      toast.error("Failed to upload profile picture. Please try again.");
    } finally {
      URL.revokeObjectURL(previewUrl);
      setIsUploadingProfilePic(false);
    }
  }

  async function handleUpdate() {
    setIsSaving(true);
    try {
      const userData = JSON.parse(getCookie("userData") || "{}");

      const res = await apiClient.post("/App/UpdateProfile", {
        editUser: {
          aadhar: formData.aadhar,
          accountName: formData.accountName,
          accountNumber: formData.accountNumber,
          address: formData.address,
          alternateMobile: formData.alternateMobile,
          bankName: formData.bankName,
          branchName: formData.branchName,
          commRate: formData.commRate,
          dob: formData.dob,
          emailID: formData.emailID,
          gstin: formData.gstin,
          ifsc: formData.ifsc,
          isCCFGstApplicable: false,
          isGSTApplicable: formData.isGSTApplicable,
          isTDSApplicable: formData.isTDSApplicable,
          landmark: formData.landmark,
          locationType: formData.locationType,
          mobileNo: formData.mobileNo,
          name: formData.name,
          outletName: formData.outletName,
          pan: formData.pan,
          pincode: formData.pincode,
          poupulation: formData.poupulation,
          qualification: formData.qualification,
          shoptype: formData.shoptype,
          userID: userData.userID || "",
        },
        ID: 0,
        IsB2C: false,
        TID: 0,
        appid: "ROUNDPAYAPPID13APR20191351",
        id: 0,
        imei: "d593bdd26dfbea56",
        loginTypeID: 1,
        regKey: "",
        serialNo: "d593bdd26dfbea56",
        session: getCookie("session") || "",
        sessionID: getCookie("sessionID") || "",
        uid: 0,
        userID: userData.userID || "",
        version: "1.0",
      });

      if (res.statuscode == 1) {
        toast.success(res.msg);
        fetchProfileData();
      } else {
        toast.error(res.msg);
      }
    } catch (err) {
      console.log("error", err);
      toast.error("Failed to update profile. Please try again.");
    } finally {
      setIsSaving(false);
    }
  }

  async function fetchKycDocuments() {
    setIsKycLoading(true);
    try {
      const userData = JSON.parse(getCookie("userData") || "{}");

      const res = await apiClient.post("/App/AppDocumentDetails", {
        ID: 0,
        IsB2C: false,
        TID: 0,
        appid: "ROUNDPAYAPPID13APR20191351",
        id: 0,
        imei: "d593bdd26dfbea56",
        loginTypeID: 1,
        regKey: "",
        serialNo: "d593bdd26dfbea56",
        session: getCookie("session") || "",
        sessionID: getCookie("sessionID") || "",
        uid: userData.userID || 0,
        userID: userData.userID || "",
        version: "1.0",
      });

      if (res.statuscode == 1) {
        setKycDocs(res.userDox || []);
      } else {
        setKycDocs([]);
        toast.error(res.msg || "Failed to load documents");
      }
    } catch (err) {
      console.log("error", err);
      toast.error("Failed to load documents. Please try again.");
    } finally {
      setIsKycLoading(false);
    }
  }

  function handleUpdateKyc() {
    setIsKycModalOpen(true);
    fetchKycDocuments();
  }

  function closeKycModal() {
    Object.values(docFiles).forEach((entry) => {
      if (entry.previewUrl) URL.revokeObjectURL(entry.previewUrl);
    });
    setIsKycModalOpen(false);
    setKycDocs([]);
    setDocFiles({});
  }

  async function handleDocFileChange(docTypeID, file) {
    if (!file) return;

    const previewUrl = file.type.startsWith("image/")
      ? URL.createObjectURL(file)
      : null;

    setDocFiles((prev) => {
      const existing = prev[docTypeID];
      if (existing?.previewUrl) URL.revokeObjectURL(existing.previewUrl);
      return {
        ...prev,
        [docTypeID]: { file, previewUrl, isUploading: true },
      };
    });

    try {
      const userData = JSON.parse(getCookie("userData") || "{}");

      const userRequest = {
        Domain: "learning2earning.net",
        docTypeID,
        ID: 0,
        IsB2C: false,
        TID: 0,
        appid: "ROUNDPAYAPPID13APR20191351",
        id: 0,
        imei: "d593bdd26dfbea56",
        loginTypeID: 1,
        regKey: "",
        serialNo: "d593bdd26dfbea56",
        session: getCookie("session") || "",
        sessionID: getCookie("sessionID") || "",
        uid: userData.userID || 0,
        userID: userData.userID || "",
        version: "1.0",
      };

      const uploadPayload = new FormData();
      uploadPayload.append("file", file, file.name);
      uploadPayload.append("userRequest", JSON.stringify(userRequest));

      const res = await apiClient.post("/App/UploadDocs", uploadPayload, {
        headers: { "Content-Type": undefined },
      });

      if (res.statuscode == 1) {
        toast.success(res.msg);
        setKycDocs((prev) =>
          prev.map((doc) =>
            doc.docTypeID === docTypeID
              ? { ...doc, docUrl: res.docImage || doc.docUrl }
              : doc,
          ),
        );
      } else {
        toast.error(res.msg);
      }
    } catch (err) {
      console.log("error", err);
      toast.error("Failed to upload document. Please try again.");
    } finally {
      setDocFiles((prev) => ({
        ...prev,
        [docTypeID]: { ...prev[docTypeID], isUploading: false },
      }));
    }
  }

  async function handleApplyForKyc() {
    setIsApplyingKyc(true);
    try {
      const userData = JSON.parse(getCookie("userData") || "{}");

      const res = await apiClient.post("/App/UpdateKYCStatus", {
        outletID: formData.outletID || 0,
        ID: 0,
        IsB2C: false,
        TID: 0,
        appid: "ROUNDPAYAPPID13APR20191351",
        id: 0,
        imei: "d593bdd26dfbea56",
        loginTypeID: 1,
        regKey: "",
        serialNo: "d593bdd26dfbea56",
        session: getCookie("session") || "",
        sessionID: getCookie("sessionID") || "",
        uid: 0,
        userID: userData.userID || "",
        version: "1.0",
      });

      if (res.statuscode == 1) {
        toast.success(res.msg);
        closeKycModal();
        fetchProfileData();
      } else {
        toast.error(res.msg);
      }
    } catch (err) {
      console.log("error", err);
      toast.error("Failed to apply for KYC. Please try again.");
    } finally {
      setIsApplyingKyc(false);
    }
  }

  if (isLoading || !formData) {
    return (
      <div className="content">
        <section className="page active">
          <p style={{ color: "var(--text-faint)" }}>Loading profile...</p>
        </section>
      </div>
    );
  }

  return (
    <div className="content">
      <section className="page active" id="page-profile">
        <h3 className="section-title">My Profile</h3>

        <div
          className="card"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 20,
            flexWrap: "wrap",
            marginBottom: 24,
          }}
        >
          <label
            className="avatar avatar-upload"
            style={{
              width: 72,
              height: 72,
              fontSize: 24,
              position: "relative",
              cursor: "pointer",
              flexShrink: 0,
            }}
          >
            {formData.profilePic ? (
              <img
                src={formData.profilePic}
                alt=""
                style={{
                  width: "100%",
                  height: "100%",
                  borderRadius: "50%",
                  objectFit: "cover",
                }}
              />
            ) : (
              getInitials(formData.name)
            )}
            <div
              className={`avatar-edit-overlay${isUploadingProfilePic ? " uploading" : ""}`}
              style={{
                position: "absolute",
                inset: 0,
                borderRadius: "50%",
                background: "rgba(0,0,0,0.55)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="#fff"
                strokeWidth="1.8"
                width={20}
                height={20}
              >
                <path d="M4 19.5A2.5 2.5 0 016.5 17H20" />
                <path d="M14.5 3.5a2.1 2.1 0 013 3L7 17l-4 1 1-4z" />
              </svg>
            </div>
            <input
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              disabled={isUploadingProfilePic}
              onChange={(e) => handleProfilePicChange(e.target.files?.[0])}
            />
          </label>
          <div style={{ flex: 1, minWidth: 200 }}>
            <h2
              style={{
                fontFamily: "var(--font-display)",
                fontSize: 22,
                fontWeight: 600,
                marginBottom: 4,
              }}
            >
              {formData.name || "-"}
            </h2>
            <span
              className={`badge ${
                formData.kycStatus === 3
                  ? "green"
                  : formData.kycStatus === 5
                    ? "red"
                    : "gold"
              }`}
            >
              KYC {KYC_STATUS_LABELS[formData.kycStatus] || "Unknown"}
            </span>
          </div>
         
        </div>

        <h3 className="section-title">Personal Information</h3>
        <div className="card" style={{ marginBottom: 24 }}>
          <div className="grid grid-3">
            <div className="field">
              <label>Name</label>
              <input
                value={formData.name}
                // onChange={(e) => handleChange("name", e.target.value)}
              />
            </div>
            <div className="field">
              <label>Date Of Birth</label>
              <input
                value={formData.dob}
                onChange={(e) => handleChange("dob", e.target.value)}
              />
            </div>
            <div className="field">
              <label>Mobile Number</label>
              <input value={formData.mobileNo} readOnly disabled />
            </div>
            <div className="field">
              <label>Alternate Mobile Number</label>
              <input
                placeholder="Enter alternate mobile no"
                value={formData.alternateMobile}
                onChange={(e) =>
                  handleChange("alternateMobile", e.target.value)
                }
              />
            </div>
            <div className="field">
              <label>Email ID</label>
              <input
                value={formData.emailID}
                onChange={(e) => handleChange("emailID", e.target.value)}
              />
            </div>
            <div className="field">
              <label>PinCode</label>
              <input
                value={formData.pincode}
                onChange={(e) => handleChange("pincode", e.target.value)}
              />
            </div>
            <div className="field">
              <label>City</label>
              <input
                value={formData.city}
                onChange={(e) => handleChange("city", e.target.value)}
              />
            </div>
            <div className="field">
              <label>State</label>
              <input
                value={formData.stateName}
                onChange={(e) => handleChange("stateName", e.target.value)}
              />
            </div>
            <div className="field">
              <label>Landmark</label>
              <input
                placeholder="Enter landmark"
                value={formData.landmark}
                onChange={(e) => handleChange("landmark", e.target.value)}
              />
            </div>
            <div className="field" style={{ gridColumn: "span 3" }}>
              <label>Address</label>
              <textarea
                placeholder="Enter address"
                style={{ minHeight: 70 }}
                value={formData.address}
                onChange={(e) => handleChange("address", e.target.value)}
              />
            </div>
          </div>
        </div>

        <h3 className="section-title">Identity Documents</h3>
        <div className="card" style={{ marginBottom: 24 }}>
          <div className="grid grid-6">
            <div className="field">
              <label>Pan No.</label>
              <input
                placeholder="Enter PAN"
                value={formData.pan}
                onChange={(e) =>
                  handleChange("pan", e.target.value.toUpperCase())
                }
              />
            </div>
            <div className="field">
              <label>AADHAR</label>
              <input
                placeholder="Enter AADHAR"
                value={formData.aadhar}
                onChange={(e) => handleChange("aadhar", e.target.value)}
              />
            </div>
          </div>
        </div>

        <h3 className="section-title">Bank Information</h3>
        <div className="card" style={{ marginBottom: 24 }}>
          <div className="grid grid-3">
            <div className="field">
              <label>Bank</label>
              <input
                value={formData.bankName}
                onChange={(e) => handleChange("bankName", e.target.value)}
              />
            </div>
            <div className="field">
              <label>Branch</label>
              <input
                placeholder="Enter branch name"
                value={formData.branchName}
                onChange={(e) => handleChange("branchName", e.target.value)}
              />
            </div>
            <div className="field">
              <label>IFSC</label>
              <input
                value={formData.ifsc}
                onChange={(e) =>
                  handleChange("ifsc", e.target.value.toUpperCase())
                }
              />
            </div>
            <div className="field">
              <label>Account Number</label>
              <input
                value={formData.accountNumber}
                onChange={(e) => handleChange("accountNumber", e.target.value)}
              />
            </div>
            <div className="field">
              <label>Account Name</label>
              <input
                value={formData.accountName}
                onChange={(e) => handleChange("accountName", e.target.value)}
              />
            </div>
          </div>
        </div>

        <div style={{ display: "flex", gap: 12 }}>
          {formData.kycStatus !== 3 && (
            <button
              type="button"
              className="btn-primary"
              style={{ width: "auto", padding: "11px 28px" }}
              onClick={handleUpdate}
              disabled={isSaving}
            >
              {isSaving ? "Saving..." : "Update"}
            </button>
          )}
          {KYC_UPDATE_ALLOWED_STATUSES.includes(formData.kycStatus) && (
            <button
              type="button"
              className="btn-primary" style={{ width: "auto", padding: "11px 28px" }}
              onClick={handleUpdateKyc}
            >
              Update KYC
            </button>
          )}
        </div>
      </section>

      {isKycModalOpen && (
        <div className="modal-overlay active">
          <div
            className="modal wide"
            style={{ maxHeight: "88vh", overflowY: "auto" }}
          >
            <span className="modal-close" onClick={closeKycModal}>
              &times;
            </span>
            <div className="modal-head">
              <h3>Document Type Detail</h3>
            </div>

            {isKycLoading ? (
              <p style={{ color: "var(--text-faint)" }}>Loading documents...</p>
            ) : kycDocs.length === 0 ? (
              <p style={{ color: "var(--text-faint)" }}>
                No documents required.
              </p>
            ) : (
              <div className="table-scroll">
                <table>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Document</th>
                      <th>Uploaded Document</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {kycDocs.map((doc, idx) => {
                      const selected = docFiles[doc.docTypeID];
                      const existingPreview = doc.docUrl
                        ? doc.docUrl.startsWith("http")
                          ? doc.docUrl
                          : `${DOC_BASE_URL}/Image/KYC/${doc.docUrl}`
                        : null;
                      const previewSrc =
                        selected?.previewUrl || existingPreview;
                      const isUploaded = !!doc.docUrl;
                      const docStatusLabel = !isUploaded
                        ? "Not Uploaded"
                        : KYC_STATUS_LABELS[doc.kycStatus] || "Uploaded";
                      const docStatusClass = !isUploaded
                        ? "red"
                        : doc.kycStatus === 3
                          ? "green"
                          : doc.kycStatus === 5
                            ? "red"
                            : "gold";

                      return (
                        <tr key={doc.docTypeID ?? idx}>
                          <td className="mono">{idx + 1}</td>
                          <td className="strong">
                            {doc.docName}
                            {!doc.isOptional && (
                              <span
                                style={{ color: "var(--red)", marginLeft: 4 }}
                              >
                                *
                              </span>
                            )}
                          </td>
                          <td>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 12,
                              }}
                            >
                              <label
                                className="btn-sm primary"
                                style={{
                                  cursor: selected?.isUploading
                                    ? "wait"
                                    : "pointer",
                                  margin: 0,
                                  opacity: selected?.isUploading ? 0.6 : 1,
                                }}
                              >
                                {selected?.isUploading
                                  ? "Uploading..."
                                  : doc.docUrl
                                    ? "Replace"
                                    : "Upload"}
                                <input
                                  type="file"
                                  accept=".jpg,.jpeg,.png,.gif,.pdf"
                                  style={{ display: "none" }}
                                  disabled={selected?.isUploading}
                                  onChange={(e) =>
                                    handleDocFileChange(
                                      doc.docTypeID,
                                      e.target.files?.[0],
                                    )
                                  }
                                />
                              </label>
                              {previewSrc ? (
                                <img
                                  src={previewSrc}
                                  alt=""
                                  style={{
                                    width: 42,
                                    height: 42,
                                    borderRadius: 7,
                                    objectFit: "cover",
                                    border: "1px solid var(--border)",
                                  }}
                                />
                              ) : (
                                selected?.file && (
                                  <span
                                    style={{
                                      fontSize: 12,
                                      color: "var(--text-faint)",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      maxWidth: 140,
                                    }}
                                  >
                                    {selected.file.name}
                                  </span>
                                )
                              )}
                            </div>
                          </td>
                          <td>
                            <span
                              className={`badge ${
                                selected?.isUploading
                                  ? "gold"
                                  : docStatusClass
                              }`}
                            >
                              {selected?.isUploading
                                ? "Uploading..."
                                : docStatusLabel}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <div
              className="field-note"
              style={{ color: "var(--text-faint)", marginTop: 14 }}
            >
              Only .jpeg, .jpg, .png, .gif, .pdf files are allowed and file size
              not more than 200 KB.
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginTop: 18,
              }}
            >
              <button
                type="button"
                className="btn-primary"
                style={{ width: "auto", padding: "11px 26px" }}
                onClick={handleApplyForKyc}
                disabled={isApplyingKyc}
              >
                {isApplyingKyc ? "Applying..." : "Apply for KYC"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
